import { IconButton, OdinIcon, OdinIconSize } from '@myosh/odin-components';
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../helpers/util';
import { useAppDispatch } from '../../redux/hooks';
import { JobStatus, removeJob, type SubmittedJobDetails } from '../../redux/slices/running-jobs';

interface RunningJobsPanelProps {
  submittedJobs: SubmittedJobDetails[];
}

const RunningJobsPanel: React.FC<RunningJobsPanelProps> = ({ submittedJobs }) => {
  const panelButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();

  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [hasViewedJobs, setHasViewedJobs] = useState(false);

  const onPanelButtonClick = () => setIsPanelVisible(!isPanelVisible);
  const onCloseButtonClick = () => setIsPanelVisible(false);

  const getPanelButtonStatusIcon = () => {
    const statusIconClasses = 'absolute top-[-10px] right-[-10px]';
    const latestJobStatus = submittedJobs[0].status;

    if (submittedJobs.some((job: SubmittedJobDetails) => job.status === JobStatus.InProgress)) {
      return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} className={statusIconClasses} spin />;
    } else if (hasViewedJobs) {
      return null;
    } else if (latestJobStatus === JobStatus.Failed) {
      return (
        <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className={`${statusIconClasses} text-error`} />
      );
    } else if (latestJobStatus === JobStatus.Success) {
      return (
        <OdinIcon icon="CircleCheck" size={OdinIconSize.ExtraSmall} className={`${statusIconClasses} text-success`} />
      );
    } else {
      return null;
    }
  };

  const onJobClick = (job: SubmittedJobDetails) => {
    if (job && job.url && job.name && job.status === JobStatus.Success) {
      window.open(job.url);
    }
  };

  const onJobRemove = (id: number) => {
    if (id) {
      dispatch(removeJob(id));
    }
  };

  const getStatusIcon = (status?: JobStatus) => {
    if (status) {
      if (status === JobStatus.InProgress) {
        return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} spin />;
      } else if (status === JobStatus.Success) {
        return <OdinIcon icon="CircleCheck" size={OdinIconSize.ExtraSmall} className="text-success" />;
      } else if (status === JobStatus.Failed) {
        return <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className="text-error" />;
      }
    }
  };

  useEffect(() => {
    if (isPanelVisible) {
      if (submittedJobs.every((job: SubmittedJobDetails) => job.status !== JobStatus.InProgress)) {
        setHasViewedJobs(true);
      } else {
        setHasViewedJobs(false);
      }
    }

    if (submittedJobs.length === 0) {
      setIsPanelVisible(false);
    }
  }, [isPanelVisible, submittedJobs]);

  return (
    <div className="flex items-center">
      {submittedJobs && submittedJobs.length > 0 && (
        <div data-cy="running-jobs-container" className="bg-mono-1 relative mt-[0.4rem] space-y-2 rounded-b-lg p-4">
          <button
            data-cy="running-jobs-toggle-button"
            ref={panelButtonRef}
            onClick={onPanelButtonClick}
            className="relative"
          >
            <OdinIcon icon="FolderDown" />
            {getPanelButtonStatusIcon()}
          </button>
          {isPanelVisible && (
            <div
              data-cy="running-jobs-panel"
              className="bg-gray-5 absolute top-10 z-4001 max-w-[25rem] min-w-[14rem] rounded-lg shadow-lg"
            >
              <div data-cy="running-jobs-header" className="flex items-center justify-between p-4">
                <span data-cy="running-jobs-header-title" className="whitespace-nowrap">
                  Job Status
                </span>
                <IconButton
                  data-cy="running-jobs-close-button"
                  aria-label="Close running jobs panel"
                  onClick={onCloseButtonClick}
                  icon="X"
                  className="rounded-sm p-1"
                />
              </div>
              <div data-cy="running-jobs-list" className="bg-mono-1 space-y-2 rounded-b-lg p-4">
                {submittedJobs.map((job: SubmittedJobDetails) => {
                  const itemStyles = cn('flex gap-2 items-center min-w-0 w-full', {
                    'hover:cursor-pointer': job.status === JobStatus.Success,
                  });

                  const removeIconStyles = cn('w-4 h-4 rounded-full hover:bg-primary-5 hover:cursor-pointer', {
                    'pointer-events-none': job.status === JobStatus.InProgress,
                  });

                  return (
                    <div
                      key={job.id}
                      data-cy={`running-job-${job.id}`}
                      className="[&:has(div:hover)]:bg-primary-5 flex items-center p-1"
                    >
                      <div
                        data-cy={`running-job-item-${job.id}`}
                        className={itemStyles}
                        onClick={() => onJobClick(job)}
                      >
                        <OdinIcon icon="FileText" />
                        <span className="flex-1 truncate" title={job.name}>
                          {job.name}
                        </span>
                        <span>{getStatusIcon(job.status)}</span>
                      </div>
                      <span
                        data-cy={`running-job-remove-${job.id}`}
                        className={removeIconStyles}
                        onClick={() => onJobRemove(job.id)}
                      >
                        {job.status !== JobStatus.InProgress && <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RunningJobsPanel;
